import moment from "moment";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LabCriteriasDTO } from "@/Common/Api/Labs/Dto/Responses/LabCriteriasDTO";
import { LabMessageDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { CacheHelper } from "@/Common/Helpers/CacheHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { SiteHelper } from "@/Common/Helpers/SiteHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { LabInboxes, LabTabViews } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { LabsHelper } from "@/Labs/Helpers/LabsHelper";
import { LabMessageTableItem } from "@/Labs/Models/LabMessageTableItem";
import { ILabModel } from "@/Labs/Models/UI/ILabModel";
import { ILabsActionInboxModel } from "@/Labs/Models/UI/ILabsActionInboxModel";
import { DefaultProvider, ILabsGlobalWrapper } from "@/Labs/Models/UI/ILabsGlobalWrapper";
import { ILabGraphsModel } from "@/Labs/Models/UI/ILabsGraphModel";
import { ILabsInboxModel } from "@/Labs/Models/UI/ILabsInboxModel";
import { ILabsTabModel } from "@/Labs/Models/UI/ILabsTabModel";
import { ISideNoteModel } from "@/Labs/Models/UI/ISideNoteModel";
import { ManualLabsInboxFilteringConfiguration } from "../Components/ManualLabsInboxSettingsContainer";
import { LabsAction } from "./Actions/LabsAction";
import { LabActionsInboxSettingsContainerHandler } from "./Handlers/Local/LabActionsInboxSettingsContainerHandler";
import { LabGraphsHandler } from "./Handlers/Local/LabGraphsHandler";
import { LabsActionsInboxViewHandler } from "./Handlers/Local/LabsActionsInboxViewHandler";
import { LabsInboxSettingsContainerHandler } from "./Handlers/Local/LabsInBoxSettingsContainerHandler";
import { LabsInboxViewHandler } from "./Handlers/Local/LabsInboxViewHandler";
import { LabsTabHandler } from "./Handlers/Local/LabsTabHandler";
import { LabViewHandler } from "./Handlers/Local/LabViewHandler";
import { LabsGraphResponseHandler } from "./Handlers/Server/LabsGraphResponseHandler";
import { LabsResponseHandler } from "./Handlers/Server/LabsResponseHandler";

let defaultInstance: LabsStore;

class LabsStore extends BaseStore {
    private mapper: RequestMapper;
    private serverMapper: ServerResponseMapper;

    private labInboxViewMethods: LabsInboxViewHandler;
    private labTabMethods: LabsTabHandler;
    private labViewHandler: LabViewHandler;
    private labGraphsHandler: LabGraphsHandler;
    private labActionsInboxSettingsContainerHandler: LabActionsInboxSettingsContainerHandler;

    private labsInBoxSettingsContainerMethods: LabsInboxSettingsContainerHandler;
    private labsActionsInboxViewHandler: LabsActionsInboxViewHandler;
    private responseHandler: LabsResponseHandler;
    private graphResponseHandler: LabsGraphResponseHandler;

    public Model: ILabsTabModel;
    public LabsInboxModel: ILabsInboxModel;
    public ManualLabFilterSettings: ManualLabsInboxFilteringConfiguration | null;
    public LabModel: ILabModel;
    public LabGraphModel: ILabGraphsModel;
    public SideNoteModel: ISideNoteModel;
    public LabsActionInboxModel: ILabsActionInboxModel;
    public LabFillerIDsViewedNaviguationPosition: number;
    public saveEncounterLabFillerId: string;
    public counterSettingsHideResultsFiledByColleague: boolean;

    public LabActions: LabsAction;

    public helper: MyleHelper;

    public timeoutReferenceRefreshLab: number;
    public timeoutReferenceRefreshLabAction: number;
    public timeoutReferenceGetLab: number;

    public labViewScrollPosition: number;

    public constructor() {
        super();
        this.timeoutReferenceRefreshLab = -1;
        this.timeoutReferenceRefreshLabAction = -1;
        this.timeoutReferenceGetLab = -1;
        this.LabFillerIDsViewedNaviguationPosition = 0;
        this.counterSettingsHideResultsFiledByColleague = false;

        this.helper = new MyleHelper();
        let self = this;
        this.LabActions = new LabsAction();

        // function mapping
        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();

        this.labInboxViewMethods = new LabsInboxViewHandler();
        this.labTabMethods = new LabsTabHandler();
        this.labsInBoxSettingsContainerMethods = new LabsInboxSettingsContainerHandler();
        this.labViewHandler = new LabViewHandler();
        this.labsActionsInboxViewHandler = new LabsActionsInboxViewHandler();
        this.labActionsInboxSettingsContainerHandler = new LabActionsInboxSettingsContainerHandler();
        this.labGraphsHandler = new LabGraphsHandler();

        this.responseHandler = new LabsResponseHandler(this.LabActions);
        this.graphResponseHandler = new LabsGraphResponseHandler(this.LabActions);

        this.mapper.AddInstance(this.labsInBoxSettingsContainerMethods);
        this.mapper.AddInstance(this.labInboxViewMethods);
        this.mapper.AddInstance(this.labTabMethods);
        this.mapper.AddInstance(this.labViewHandler);
        this.mapper.AddInstance(this.labsActionsInboxViewHandler);
        this.mapper.AddInstance(this.labGraphsHandler);
        this.mapper.AddInstance(this.labActionsInboxSettingsContainerHandler);

        this.serverMapper.AddInstance(this.responseHandler);
        this.serverMapper.AddInstance(this.graphResponseHandler);

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => { self.serverMapper.Map(response, self); });

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => self.mapper.Map(request, self));

        this.setDefaultModels();
    }

    public static createSingleton(): LabsStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new LabsStore());
    }

    public static createSingletonFrom(store: LabsStore, copy: boolean = true): LabsStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    public setDefaultModels(): void {
        var speciality: FlatLookupView = null;
        var specialisations = sessionStore.getCurrentEmployee().getServiceSpecializations();
        if (specialisations && specialisations.length > 0) { speciality = specialisations[0]; }

        var defaultModel = LabsHelper.GetDefaultForLabsTabModel();
        defaultModel.CurrentSpeciality = speciality;
        defaultModel.Action_CheckboxesNurses = cacheStore.GetCheckboxesByLookupTypeCode("LabActions", null, false).filter(y => y.Position >= 0 && y.Position <= 7).sort(x => x.Position);
        defaultModel.Action_CheckboxesSecretaries = cacheStore.GetCheckboxesByLookupTypeCode("LabActions", null, false).filter(y => y.Position >= 9).sort(x => x.Position);
        this.Model = Immutable.fromJS(defaultModel);

        var defaultLabsInboxViewModel = LabsHelper.GetDefaultForLabsInboxModel();
        defaultLabsInboxViewModel.GlobalLabsearchPlaceholder = cacheStore.GetString("SearchLabel");
        this.LabsInboxModel = Immutable.fromJS(defaultLabsInboxViewModel);

        var defaultLabsViewModel = LabsHelper.GetDefaultForLabModel();
        this.LabModel = Immutable.fromJS(defaultLabsViewModel);

        var defaultlabsActionInboxViewModel = LabsHelper.GetDefaultForLabsActionInboxModel();
        defaultlabsActionInboxViewModel.GlobalActionsearchPlaceholder = cacheStore.GetString("SearchLabel");
        this.LabsActionInboxModel = Immutable.fromJS(defaultlabsActionInboxViewModel);

        var defaultLabGraphsModel = LabsHelper.GetDefaultForLabGraphModel();
        this.LabGraphModel = Immutable.fromJS(defaultLabGraphsModel);

        var sideNoteDefaultModel = LabsHelper.GetDefaultForSideNoteModel();
        this.SideNoteModel = Immutable.fromJS(sideNoteDefaultModel);
    }

    public getCliniciansFromSelectedSites(clinicianCache: Immutable.List<EmployeeUIModel>, _checkedSites: Immutable.List<SiteUIModel>): Immutable.List<EmployeeUIModel> {
        var checkedSiteIds = this.helper.GetCheckedItemsFromICheckableList(_checkedSites).map((site: SiteUIModel) => site.Id).toArray();
        var allClinicians = clinicianCache;
        return SearchMenuHelper.FilterCliniciansMembersOfSites(allClinicians, checkedSiteIds);
    }

    public getCliniciansBasedOnSitesAndQuery(searchText: string, cliniciansCache: Immutable.List<EmployeeUIModel>, activeEmployeeId: Immutable.List<string>, _checkedSites: Immutable.List<SiteUIModel>): Immutable.List<EmployeeUIModel> {
        var activeClinician: Immutable.List<EmployeeUIModel> = null;
        if (activeEmployeeId != null) {
            activeClinician = cliniciansCache.filter((y: EmployeeUIModel) => activeEmployeeId.contains(y.ContactFacetId)).toList();
        }
        else { activeClinician = cliniciansCache; }

        var cliniciansFromSites = this.getCliniciansFromSelectedSites(activeClinician, _checkedSites);
        var filterString = searchText.trim();
        if (filterString !== "") { cliniciansFromSites = SearchMenuHelper.FilterCliniciansByName(filterString, cliniciansFromSites); }
        return cliniciansFromSites;
    }

    public findClinician(id: string): Employee {
        return cacheStore.GetEmployeeByContactIdOrDefault(id) ?? new Employee();
    }

    public GetLabFillerIdByTablePosition(positionZeroBased: number): string {
        return this.GetLabByTablePosition(positionZeroBased).LabFillerID;
    }

    public GetLabByTablePosition(positionZeroBased: number): LabMessageDTO {
        return this.LabsInboxModel.get("LabsTableData").get(positionZeroBased).Model.Model;
    }

    public GetLabInboxCriterias(): LabCriteriasDTO {
        let criterias = new LabCriteriasDTO();
        criterias.Text = this.LabsInboxModel.get("GlobalLabsearchText");
        criterias.Page = this.LabsInboxModel.get("CurrentPage");
        criterias.PageSize = this.LabsInboxModel.get("SelectedPageSizeOption");
        criterias.From = this.LabsInboxModel.get("DateFrom") == null ? null : moment(this.LabsInboxModel.get("DateFrom")).format("YYYY-MM-DD HH:mm:ss");
        criterias.To = this.LabsInboxModel.get("DateTo") == null ? null : moment(this.LabsInboxModel.get("DateTo")).format("YYYY-MM-DD HH:mm:ss");
        criterias.Abnormality = null;

        criterias.Abnormality = this.LabsInboxModel.get("ShowAbnormalsOnly");

        criterias.ToConsentDate = new Date(2050, 1, 1, 0, 0, 0, 0);
        criterias.sortdirection = this.LabsInboxModel.get("SortColumnDirection");

        criterias.LicenseNumbers = this.LabsInboxModel.get("CliniciansGlobalCache")
            .filter(emp => emp.Checked === true && emp.Visible === true)
            .map(emp => emp.getLicenseDto())
            .toArray();

        // Yourself
        criterias.UserLicenseNumber = sessionStore.getCurrentEmployeeLicenseDto();
        criterias.ClinicianType = [];
        const isIamPrescriberSelected = this.LabsInboxModel.get("IamPrescriberSelected");
        const isIamInCcSelected = this.LabsInboxModel.get("IamInCcSelected");

        if (isIamPrescriberSelected)
            criterias.ClinicianType.push(4);

        if (isIamInCcSelected) {
            criterias.ClinicianType.push(5);
            criterias.ClinicianType.push(6);
        }
        // -------

        criterias.IsSpecificSearch = false; // Only the Labs in Specific LabsIDs
        criterias.SpecificLabsIDs = null;

        // DB 30/11/2016
        // integration of labs into patient tab
        // ***************************************************************************
        // id is the patient contact id (ActivePatientContactId in patientStore)
        // and only set if current model set is inside patient file
        // dependency on patientStore :(
        criterias.IsPatientSearch = !!this.Model.get("PatientContactId");
        if (criterias.IsPatientSearch)
            criterias.PatientContactId = this.Model.get("PatientContactId");

        criterias.IsNew = null;
        criterias.IsSubmitted = null;
        criterias.IsClassified = null;

        if (this.LabsInboxModel.get("CurrentInbox") == LabInboxes.New)
            criterias.IsNew = true;

        if (this.LabsInboxModel.get("CurrentInbox") == LabInboxes.Suspended)
            criterias.IsSubmitted = true;

        if (this.LabsInboxModel.get("CurrentInbox") == LabInboxes.Classified)
            criterias.IsClassified = true;

        criterias.IgnoreContextFilterForCurrentUser = false;
        criterias.ContextFilter = SiteHelper.GetContexts();

        criterias.PortalSyncStatuses = this.LabsInboxModel.get("PortalStatuses").toArray().filter(x => x.Checked).map(x => parseInt(x.Id));
        if (criterias.PortalSyncStatuses.length == this.LabsInboxModel.get("PortalStatuses").count())
            criterias.PortalSyncStatuses = null;
        criterias.PortalSyncUserContactIds = this.LabsInboxModel.get("SentByGlobalCache").toArray().filter(x => x.Checked).map(x => x.ContactId);
        if (criterias.PortalSyncUserContactIds.length == this.LabsInboxModel.get("SentByGlobalCache").count())
            criterias.PortalSyncUserContactIds = null;

        criterias.ShowEnrolledPatientsResults = this.LabsInboxModel.get("ShowEnrolledPatientsResults");
        criterias.HideResultsFiledByColleague = this.LabsInboxModel.get("HideResultsFiledByColleague");
        criterias.AlwaysShowAbnormalResults = this.LabsInboxModel.get("AlwaysShowAbnormalResults");
        criterias.SelectedLabProcessingStatuses = this.LabsInboxModel.get("SelectedLabProcessingStatuses")?.toArray();

        return criterias;
    }

    public setManualLabsInboxFilteringConfiguration(filter: ManualLabsInboxFilteringConfiguration): void {
        this.ManualLabFilterSettings = { ...filter };
    }

    public getManualLabsInboxFilteringConfiguration(): ManualLabsInboxFilteringConfiguration {
        if (!this.ManualLabFilterSettings) {
            this.ManualLabFilterSettings = new ManualLabsInboxFilteringConfiguration();
        }
        return this.ManualLabFilterSettings;
    }

    public rebindCurrentEmployee(): void {
        let updatedEmployees = this.LabsInboxModel.get("CliniciansGlobalCache")
            .mutate(p => {
                const currentEmployee = cacheStore.GetCurrentEmployee();
                if (p.ContactFacetId === currentEmployee.ContactFacet_id) {
                    p.Model.Licences = currentEmployee.Licences;
                    p.Model.FirstLicenceNumber = currentEmployee.Licences?.[0]?.LicenceNumber;
                }
            })
            .toArray();

        this.LabsInboxModel = this.LabsInboxModel.set("CliniciansGlobalCache", Immutable.List(updatedEmployees));
    }

    public GetSharedOrphansLicences(): string[] { return []; }

    public ResetGraphsModel(): void {
        const graphDefault = LabsHelper.GetDefaultForLabGraphModel();
        this.LabGraphModel = Immutable.fromJS(graphDefault);
    }

    public filterTableItems(): void {
        const filteredPrescriptorLicences = labsStore.LabsActionInboxModel
            .get("PrescriptorClinicians")
            .filter(clinician => clinician.Checked && clinician.Visible)
            .map(clinician => clinician.Model.FirstLicenceNumber)
            .toList();

        const filteredActionByContactFacetId = labsStore.LabsActionInboxModel
            .get("ActionByClinicians")
            .filter(clinician => clinician.Checked && clinician.Visible)
            .map(clinician => clinician.Model.ContactFacet_id)
            .toList();

        const skipFilter1 = labsStore.LabsActionInboxModel.get("SelectAllCliniciansPrescriptorChecked");
        const skipFilter2 = labsStore.LabsActionInboxModel.get("SelectAllCliniciansActionByChecked");

        const tableItemFilter1 = labsStore.LabsActionInboxModel
            .get("ActionsTableDataFull")
            .filter(item => {
                const prescriptor = item.Model?.getPrescriptorDTO();

                return skipFilter1
                    || prescriptor == null // include bugged labs
                    || filteredPrescriptorLicences.contains(prescriptor.ID_Number);
            });

        const tableItemFilter2 = tableItemFilter1.filter(item =>
            skipFilter2
            || item.ActionByEmployeesContactFacetId.some(p => filteredActionByContactFacetId.contains(p))
        );

        this.LabsActionInboxModel = this.LabsActionInboxModel.set("ActionsTableData", Immutable.List<LabMessageTableItem>(tableItemFilter2));
        this.LabsActionInboxModel = this.LabsActionInboxModel.set("TotalQueryRowsFiltered", tableItemFilter2.size);
    }

    public ReturnStateToMainTab(): void {
        if (cacheStore.LabsCachedState != null)
            this.ImportLabsModels(cacheStore.LabsCachedState);
        else {
            this.setDefaultModels();
        }
        labsStore.LoadSettings(false);
    }

    public SaveState(): void {
        if (this.LabsInboxModel?.get("IsFromPatient"))
            patientStore.saveLabsStoreState();
        else
            cacheStore.LabsCachedState = this.ExportLabModels();
    }

    public getCurrentModelPatientId(): string {
        return this.LabModel?.get("PatientContactId") ?? "";
    }

    public ExportLabModels(): ILabsGlobalWrapper {
        var m = Immutable.fromJS(DefaultProvider.Default());
        m = m.set("Model", this.Model);
        m = m.set("LabsInboxModel", this.LabsInboxModel);
        m = m.set("LabModel", this.LabModel);
        m = m.set("LabGraphModel", this.LabGraphModel);
        m = m.set("SideNoteModel", this.SideNoteModel);
        m = m.set("LabsActionInboxModel", this.LabsActionInboxModel);
        m = m.set("LabFillerIDsViewedNaviguationPosition", this.LabFillerIDsViewedNaviguationPosition);
        m = m.set("SaveEncounterLabFillerId", this.saveEncounterLabFillerId);
        return m;
    }

    public ImportLabsModels(model: ILabsGlobalWrapper): void {
        if (model == null)
            return;

        this.Model = model.get("Model");
        this.LabsInboxModel = model.get("LabsInboxModel");
        this.LabModel = model.get("LabModel");
        this.LabGraphModel = model.get("LabGraphModel");
        this.SideNoteModel = model.get("SideNoteModel");
        this.LabsActionInboxModel = model.get("LabsActionInboxModel");
        this.LabFillerIDsViewedNaviguationPosition = model.get("LabFillerIDsViewedNaviguationPosition");
        this.saveEncounterLabFillerId = model.get("SaveEncounterLabFillerId");
    }

    public ShowLabTab(): void {
        window.setTimeout(() => {
            RouterHistory.push("results");
            this.EmitChangeToListeners();
        }, 0);
    }

    public RefreshSearch(): void {
        if (labsStore.Model != null && labsStore.Model.get("CurrentView") != LabTabViews.Lab) // do not refresh if we are viewing a lab
            this.LabActions.RefreshLabs(); // this will emit change when ready
    }

    public UpdateVisibilityToCliniciansFromSelectedSites(fullList: Immutable.List<EmployeeUIModel>): Immutable.List<EmployeeUIModel> {
        var checkedSiteIds = this.SelectedSites().map((s: SiteUIModel) => s.Id).toArray();
        var activeState = this.LabsInboxModel.get("ShowDeactivatedClinicians");
        var list = fullList.map(c => {
            let include = !checkedSiteIds.any() || c.Sites.any(s => checkedSiteIds.contains(s));

            c.Visible = include && (activeState || !c.Model.IsInactive);
            return c;
        });
        return list.toList();
    }

    public SelectedSites(): Immutable.List<SiteUIModel> {
        return this.LabsInboxModel.get("Sites").filter((site: SiteUIModel) => site.Checked === true).toList();
    }

    public CloseActionNotePopup(store: LabsStore): void {
        if (store.Model.get("NoteWindowOpen")) {
            this.NotesDropdownClose(null, store);
        }

        if (store.Model.get("Action_NoteWindowOpen")) {
            this.ActionNotesDropdownClose(null, store);
        }

        if (store.Model.get("Action_DetailsWindowOpen")) {
            this.ActionDetailsDropdownClose(null, store);
        }

        if (store.Model.get("ResultDetailPopupOpen")) {
            this.closeResultDetailPopup(null, store);
        }
    }

    public ToggleActionCheckbox(store: LabsStore, lookupId: string, IsChecked: boolean = null): void {
        // ToggleCheckBox
        var actionNurses = store.Model.get("Action_CheckboxesNurses");
        var actionSecretaries = store.Model.get("Action_CheckboxesSecretaries");

        var newActionsNurses = actionNurses.map(y => {
            if (y.Id == lookupId) // Toggle Current Id
                y.Checked = !y.Checked;
            else if (lookupId == null) // Force State for All
                y.Checked = IsChecked;

            return y;
        }).toList();
        var newActionSecretaries = actionSecretaries.map(y => {
            if (y.Id == lookupId) // Toggle Current Id
                y.Checked = !y.Checked;
            else if (lookupId == null) // Force State for All
                y.Checked = IsChecked;

            return y;
        }).toList();

        store.Model = store.Model.delete("Action_CheckboxesNurses");
        store.Model = store.Model.set("Action_CheckboxesNurses", newActionsNurses);

        store.Model = store.Model.delete("Action_CheckboxesSecretaries");
        store.Model = store.Model.set("Action_CheckboxesSecretaries", newActionSecretaries);
    }

    public GetCleanId(str: string): string {
        // JQuery c3 chart Fix for any lab ids containing colons. i.e CD:999123 --> CD999123
        return str && str.trim().replaceAll(":", "_c_").replaceAll("%", "_p_").replaceAll("#", "_n_").replaceAll(".", "_p_");
    }

    public LoadSettings(isFromPatient: boolean): void {
        if (this.LabsActionInboxModel.get("Initialized") === true && this.LabsInboxModel.get("Initialized") == true)
            return;

        this.LabsInboxModel = this.LabsInboxModel.set("IsFromPatient", isFromPatient);

        // The user should not be able to save settings from the patient tab
        const labSettings = isFromPatient ? null : cacheStore.UserSettings?.LabSettings;

        let cliniciansCache = CacheHelper
            .LoadClinicianUICache(CheckboxTarget.Myle_Labs_Clinicians, true, isFromPatient)
            .filter(clinician => !IsNullOrWhiteSpace(clinician.getLicenseDto()?.LicenseNumber));

        const labsPortalSentBy = CacheHelper.LoadClinicianUICache(CheckboxTarget.Myle_Labs_PortalSentBy, true, isFromPatient);
        const portalStatuses = PortalHelper.getPortalStatusCheckableList(CheckboxTarget.Myle_Labs_PortalStatus);
        const clinicianList1Actions = CacheHelper.LoadActiveClinicianUICache(CheckboxTarget.Myle_Labs_Clinicians, false, true);
        const clinicianList2Actions = CacheHelper.LoadActiveClinicianUICache(CheckboxTarget.Myle_Labs_Clinicians_List2, false, true);

        this.LabsActionInboxModel = this.LabsActionInboxModel.set("PrescriptorClinicians", Immutable.List(clinicianList1Actions));
        this.LabsActionInboxModel = this.LabsActionInboxModel.set("ActionByClinicians", Immutable.List(clinicianList2Actions));

        let savedSentByIds = labSettings?.PortalSentByIds ?? [];
        let savedStatuses = labSettings?.PortalStatusesIds ?? [];
        let sites = CacheHelper.LoadSitesFromCache(CheckboxTarget.Myle_Labs_Sites);

        if (!isFromPatient) {
            let savedEmpIds = labSettings?.EmployeeIds;

            if (isArrayNullOrUndefinedOrEmpty(savedEmpIds)) // Never had settions before, add current employee by default
                savedEmpIds = [sessionStore.getCurrentEmployee().ContactFacetId];

            if (labSettings?.SiteIds?.any())
                sites.forEach(site => site.Checked = !!labSettings?.SiteIds.some(e => e === site.Id));

            cliniciansCache = this.UpdateVisibilityToCliniciansFromSelectedSites(cliniciansCache.toImmutable()).toArray();
            cliniciansCache.forEach(clinician => clinician.Checked = savedEmpIds.some(p => p == clinician.ContactFacetId));

            this.LabsInboxModel = this.LabsInboxModel.set(
                "HideResultsFiledByColleague",
                labSettings?.HideResultsFiledByColleagueChecked
                ?? this.LabsInboxModel.get("HideResultsFiledByColleague")
            );

            this.counterSettingsHideResultsFiledByColleague = this.LabsInboxModel.get("HideResultsFiledByColleague");
        }

        this.LabsInboxModel = this.LabsInboxModel.set("Sites", Immutable.List(sites));
        this.LabsActionInboxModel = this.LabsActionInboxModel.set("Sites", Immutable.List(sites));

        labsPortalSentBy.forEach(sentBy => {
            sentBy.Checked = savedSentByIds.some(p => p == sentBy.ContactFacetId);
        });

        portalStatuses.forEach(status => {
            status.Checked = savedStatuses.some(p => p == status.Id);
        });

        // apply saved settings
        this.LabsInboxModel = this.LabsInboxModel
            .set(
                "SelectAllSitesChecked",
                sites?.filter(s => s.Visible).all(s => s.Checked)
                ?? this.LabsInboxModel.get("SelectAllSitesChecked")
            )
            .set(
                "SelectAllCliniciansChecked",
                cliniciansCache?.filter(c => c.Visible).all(c => c.Checked)
                ?? this.LabsInboxModel.get("SelectAllCliniciansChecked")
            )
            .set(
                "ShowDeactivatedClinicians",
                (labSettings?.IsDeactivatedChecked || isFromPatient)
                ?? this.LabsInboxModel.get("ShowDeactivatedClinicians")
            )
            .set(
                "SelectAllPortalSentBy",
                labSettings?.IsAllPortalSentByChecked
                ?? this.LabsInboxModel.get("SelectAllPortalSentBy")
            )
            .set(
                "SelectAllPortalStatus",
                labSettings?.IsAllPortalStatusChecked
                ?? this.LabsInboxModel.get("SelectAllPortalStatus")
            )
            .set("CliniciansGlobalCache", Immutable.List(cliniciansCache))
            .set("SentByGlobalCache", Immutable.List(labsPortalSentBy))
            .set("PortalStatuses", Immutable.List(portalStatuses))
            .set(
                "ShowAbnormalsOnly",
                labSettings?.IsOnlyAbnormalResults
                ?? this.LabsInboxModel.get("ShowAbnormalsOnly")
            )
            .set(
                "ShowEnrolledPatientsResults",
                labSettings?.IsShowEnrolledPatientsResults
                ?? this.LabsInboxModel.get("ShowEnrolledPatientsResults")
            )
            .set(
                "SelectedPageSizeOption",
                labSettings?.PageSize > 0
                    ? labSettings?.PageSize
                    : this.LabsInboxModel.get("SelectedPageSizeOption")
            )
            .set("AlwaysShowAbnormalResults", labSettings?.AlwaysShowAbnormalResults ?? false)
            .set("SelectedLabProcessingStatuses", Immutable.List(labSettings?.SelectedLabProcessingStatuses));

        this.LabsInboxModel = this.LabsInboxModel.set("Initialized", true);
        this.LabsActionInboxModel = this.LabsActionInboxModel.set("Initialized", true);
    }

    private NotesDropdownClose(request: LocalEvent, store: LabsStore): void {
        store.Model = store.Model.set("NoteWindowOpen", false);
        store.Model = store.Model.set("CurrentLabNoteValue", "");
        store.Model = store.Model.set("CurrentLabIDForNotes", "");
        store.Model = store.Model.set("CurrentElementIDForNotes", "");
        store.Model = store.Model.set("CurrentLabNotes", Immutable.List([]));
    }

    private ActionNotesDropdownClose(request: LocalEvent, store: LabsStore): void {
        store.Model = store.Model.set("Action_NoteWindowOpen", false);
        store.Model = store.Model.set("Action_CurrentLabIDForNotes", "");
        store.Model = store.Model.set("Action_CurrentElementIDForNotes", "");
        store.Model = store.Model.set("Action_CurrentLabNoteValue", "");
        store.Model = store.Model.set("Action_OriginalOBXValue", "");
        this.ToggleActionCheckbox(store, null, false);
    }

    private ActionDetailsDropdownClose(request: LocalEvent, store: LabsStore): void {
        store.Model = store.Model.set("Action_DetailsWindowOpen", false);
        store.Model = store.Model.set("Action_CurrentLabIDForActionDetails", "");
        store.Model = store.Model.set("Action_DetailsModel", null);
    }

    private closeResultDetailPopup(_: LocalEvent, store: LabsStore): void {
        store.Model = store.Model.set("ResultDetailPopupOpen", false);
        store.Model = store.Model.set("CurrentLabNoteValue", "");
        store.Model = store.Model.set("CurrentLabIDForNotes", "");
        store.Model = store.Model.set("CurrentElementIDForNotes", "");
        store.Model = store.Model.set("CurrentLabNotes", Immutable.List([]));
        store.Model = store.Model.set("CurrentLabDetailsValue", "");
    }
}

export { LabsStore, defaultInstance as labsStore };
